import { toDisplayRating } from "@rally/rating";
import { useCallback, useEffect, useRef, useState } from "react";
import type { RatingPoint } from "../lib/ratings.js";

/**
 * Your rating over time.
 *
 * Drawn rather than charted: a charting library would be more bytes than the rest of the
 * app for one line, and would arrive with its own gridlines, boxes and shadows to be argued
 * out of (docs/design-direction.md §4). This is a polyline and two rules.
 *
 * The line is Sun Glare where the rating is settled and Blue Violet where it was still
 * provisional, which is the same sentence the lockup and the leaderboard already say. The
 * shape of the early climb is the interesting part of anyone's history — you can see the
 * ten matches it took to find your level — and it should be legible as "we were still
 * working you out" rather than as "you improved enormously".
 *
 * The geometry is measured rather than stretched. The first version used a fixed 100x40
 * viewBox with `preserveAspectRatio="none"`, which scales x and y by different factors: the
 * end-point circle came out as a clipped ellipse hooking off the right edge. Nothing warns
 * you about that — `vector-effect` keeps the stroke honest and lets the shape lie. Real
 * pixel coordinates mean a circle is a circle and a 2px stroke is 2px.
 */
export function RatingHistory({ points, height = 96 }: { points: RatingPoint[]; height?: number }) {
  const [width, setWidth] = useState(0);
  const node = useRef<HTMLDivElement | null>(null);
  const watching = useRef<ResizeObserver | null>(null);

  const measure = useCallback(() => {
    if (node.current) setWidth(node.current.getBoundingClientRect().width);
  }, []);

  /**
   * A callback ref, not `useRef` + `useEffect([])`. The effect version ran once on mount --
   * when `points` was still empty and the early return below meant there was no element to
   * observe. It found null, never ran again, and the chart stayed stuck at the fallback
   * width: right on a phone by coincidence, half-width on a desktop.
   *
   * A callback ref fires whenever the node attaches or detaches, which is the event this
   * actually cares about.
   */
  const box = useCallback(
    (element: HTMLDivElement | null) => {
      watching.current?.disconnect();
      node.current = element;
      if (!element) return;

      watching.current = new ResizeObserver(measure);
      watching.current.observe(element);
      measure();
    },
    [measure],
  );

  // Belt and braces. The observer alone did not re-fire when the viewport changed and the
  // container went from 640 to 343 -- the svg kept its old width and overflowed. Rather
  // than depend on one mechanism for the geometry everything else is derived from, listen
  // for both; `measure` is idempotent, so a double notification costs a no-op render.
  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  // One point is a dot, not a history. Two is the minimum that has a direction.
  if (points.length < 2) {
    return (
      <p className="font-mono text-xs text-ink-muted">
        {points.length === 0
          ? "No rated matches yet."
          : "One match so far — the line starts at two."}
      </p>
    );
  }

  const values = points.map((p) => p.r);
  const low = Math.min(...values);
  const high = Math.max(...values);
  // A flat history would divide by zero and, worse, draw a line through the middle of an
  // invented range. Padding a flat series keeps it flat and centred.
  const span = high - low || 1;

  // Enough room for the end marker and its stroke, so nothing is clipped at either edge.
  const pad = 5;
  const w = width || 320;
  const plotW = Math.max(w - pad * 2, 1);
  const plotH = Math.max(height - pad * 2, 1);

  const x = (i: number) => pad + (i / (points.length - 1)) * plotW;
  const y = (r: number) => pad + plotH - ((r - low) / span) * plotH;

  const path = (from: number, to: number) =>
    points
      .slice(from, to)
      .map((p, i) => `${i === 0 ? "M" : "L"} ${x(from + i).toFixed(2)} ${y(p.r).toFixed(2)}`)
      .join(" ");

  // The provisional stretch is always the start: rd only falls as matches accumulate.
  // `+ 1` so the two paths share a vertex and there is no gap where they meet.
  const settledFrom = points.findIndex((p) => !p.provisional);
  const provisionalTo = settledFrom === -1 ? points.length : settledFrom + 1;

  const first = points[0]!;
  const last = points[points.length - 1]!;
  const change = toDisplayRating(last.r) - toDisplayRating(first.r);

  return (
    <figure className="m-0">
      <div ref={box}>
        <svg
          viewBox={`0 0 ${w} ${height}`}
          width={w}
          height={height}
          style={{ display: "block" }}
          role="img"
          aria-label={
            `Rating over ${points.length} matches: ` +
            `${toDisplayRating(first.r).toFixed(3)} to ${toDisplayRating(last.r).toFixed(3)}`
          }
        >
          {provisionalTo > 1 ? (
            <path
              d={path(0, provisionalTo)}
              fill="none"
              stroke="var(--color-provisional-fill)"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ) : null}

          {settledFrom !== -1 ? (
            <path
              d={path(settledFrom, points.length)}
              fill="none"
              stroke="var(--color-line)"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ) : null}

          {/* Where you are now. The only point that gets a mark, because it is the only one
              anyone is looking for. */}
          {/* The one place the highlighter touches the chart: a lime dot ringed in ink. */}
          <circle
            cx={x(points.length - 1)}
            cy={y(last.r)}
            r={5}
            fill={last.provisional ? "var(--color-provisional-tint)" : "var(--color-highlight)"}
            stroke={last.provisional ? "var(--color-provisional)" : "var(--color-line)"}
            strokeWidth={2}
          />
        </svg>
      </div>

      <div className="mt-2 h-px" style={{ background: "var(--color-line-muted)" }} />

      <figcaption className="font-mono mt-2 flex justify-between gap-3 text-xs text-ink-muted">
        <span>{toDisplayRating(low).toFixed(3)} low</span>
        <span>
          {points.length} matches
          {change === 0 ? null : (
            <>
              {" "}
              <span
                className={change > 0 ? "highlight px-1" : ""}
                style={{ color: change > 0 ? undefined : "var(--color-down)" }}
              >
                {change > 0 ? "▲" : "▼"} {Math.abs(change).toFixed(3)}
              </span>
            </>
          )}
        </span>
        <span>{toDisplayRating(high).toFixed(3)} high</span>
      </figcaption>
    </figure>
  );
}
